import { SignInMethod } from '@/renderer/types';
import { isElectron } from '@/renderer/util';
import { KeyRound, Mail } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface AuthMethodModalProps {
  onSetAuthMethod: (val: SignInMethod) => void;
}

export const AuthMethodModal: React.FC<AuthMethodModalProps> = ({
  onSetAuthMethod,
}) => {
  const [showMethodPopup, setShowMethodPopup] = useState(false);

  useEffect(() => {
    if (isElectron()) {
      const savedMethod = localStorage.getItem('auth_method') as SignInMethod;
      if (!savedMethod) {
        setShowMethodPopup(true);
      } else {
        onSetAuthMethod(savedMethod);
      }
    } else {
      onSetAuthMethod('email');
    }
  }, [onSetAuthMethod]);

  const handleSelectMethod = (method: SignInMethod) => {
    if (method) localStorage.setItem('auth_method', method);
    onSetAuthMethod(method);
    setShowMethodPopup(false);
  };

  if (!showMethodPopup) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 border border-gray-100 flex flex-col gap-6 animate-fadeInUp">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900">
            Select Login Method
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Choose the preferred authentication method for this terminal device.
            You can change this setting at any time.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {/* Option: Email & Password */}
          <button
            type="button"
            onClick={() => handleSelectMethod('email')}
            className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-pos-primary hover:bg-green-50/30 transition-all text-left group"
          >
            <div className="w-10 h-10 rounded-lg bg-green-100 text-pos-primary flex items-center justify-center shrink-0">
              <Mail size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 group-hover:text-pos-primary transition-colors text-sm">
                Email & Password
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Log in using your registered business email and password. Ideal
                for admins.
              </p>
            </div>
          </button>

          {/* Option: 4-Digit Passcode */}
          <button
            type="button"
            onClick={() => handleSelectMethod('code')}
            className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-pos-primary hover:bg-green-50/30 transition-all text-left group"
          >
            <div className="w-10 h-10 rounded-lg bg-green-100 text-pos-primary flex items-center justify-center shrink-0">
              <KeyRound size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 group-hover:text-pos-primary transition-colors text-sm">
                4-Digit Passcode
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Log in quickly using a fast 4-digit numeric code. Ideal for
                cashiers and waiters.
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};
